import React, { useState, useEffect } from 'react';
import { getOrder, approveMetadata, rejectOrder } from '../../services/liveStamping';
import { toast } from 'react-toastify';
import { formatDateTimeIST } from '../../utils/dateUtils';

const AdminMetadataReview = ({ order, onSuccess, onClose }) => {
  const [fullOrder, setFullOrder] = useState(order);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState('');

  const isActionable = fullOrder.status === 'preview_submitted';

  useEffect(() => {
    getOrder(order.id)
      .then(res => setFullOrder(res.order || res))
      .catch(() => {})
      .finally(() => setLoadingOrder(false));
  }, [order.id]);

  const handleApprove = async () => {
    setSubmitting(true);
    setError('');
    try {
      await approveMetadata(order.id);
      toast.success('Preview approved — client notified');
      onSuccess();
    } catch (err) {
      setError(err.message || 'Approval failed.');
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    setError('');
    try {
      await rejectOrder(order.id, rejectReason.trim() || undefined);
      toast.success('Order rejected');
      onSuccess();
    } catch (err) {
      setError(err.message || 'Rejection failed.');
      setSubmitting(false);
    }
  };

  const meta = fullOrder.stamp_metadata || {};

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-bold text-gray-800">Stage 1 — Preview Review</h3>
            <p className="text-xs text-gray-500 mt-0.5">Order #{order.id} · {order.service_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingOrder ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: preview image */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Preview Image</p>
                {fullOrder.preview_base64 ? (
                  <img
                    src={`data:${fullOrder.preview_content_type || 'image/png'};base64,${fullOrder.preview_base64}`}
                    alt="Vendor preview"
                    className="w-full rounded-lg border border-gray-200 shadow-sm object-contain max-h-72"
                  />
                ) : (
                  <div className="w-full h-48 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400 text-sm">
                    No preview uploaded
                  </div>
                )}
              </div>

              {/* Right: metadata */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Order Details</p>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">Client</dt>
                    <dd className="font-medium text-gray-800">{fullOrder.client_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Vendor</dt>
                    <dd className="font-medium text-gray-800">{fullOrder.vendor_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Service</dt>
                    <dd className="font-medium text-gray-800">{fullOrder.service_name || '—'}</dd>
                  </div>
                  {meta.certificate_number && (
                    <div>
                      <dt className="text-xs text-gray-500">Certificate #</dt>
                      <dd className="font-mono text-gray-800">{meta.certificate_number}</dd>
                    </div>
                  )}
                  {meta.issue_date && (
                    <div>
                      <dt className="text-xs text-gray-500">Issue Date</dt>
                      <dd className="text-gray-800">{formatDateTimeIST(meta.issue_date)}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-xs text-gray-500">Status</dt>
                    <dd className="text-gray-800 capitalize">{fullOrder.status?.replace(/_/g, ' ')}</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* Reject textarea */}
          {rejecting && isActionable && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rejection reason (optional)</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Describe the issue so the vendor can correct it…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              />
            </div>
          )}

          {error && (
            <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center shrink-0 gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Close
          </button>
          {isActionable && (
            <div className="flex gap-2">
              {!rejecting ? (
                <>
                  <button
                    onClick={() => setRejecting(true)}
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={submitting || loadingOrder}
                    className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    Approve Preview
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setRejecting(false)} disabled={submitting} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={submitting}
                    className="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    Confirm Reject
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMetadataReview;
